/**
 * Captured-image viewer + editor.
 *
 * Shows the still from the evidence's presigned `download_url` with a
 * SEPARATE SVG overlay of the annotations bound to it. The image bytes
 * themselves are always clean — `captureFrameAsBlob` only draws the
 * <video> element's frame into the canvas; the SVG overlay never gets
 * baked in. This modal is what renders them composited for review, so
 * you can toggle the overlay off any time to see the raw frame.
 *
 * Editor mode lets the operator add NEW annotations after capture.
 * Each new annotation is POSTed to the session's annotation endpoint,
 * then immediately bound to this evidence via the bind endpoint, so it
 * persists tied to the still. Visually new + capture-time annotations
 * are identical — the audit trail keeps each one's `created_at` so the
 * auditor can tell what was drawn during the live session vs added
 * after the fact.
 *
 * Recordings (mime: video/*) still render with native <video> playback
 * and no overlay — the worker burns annotations into pixels at the
 * recording's own per-frame timestamps.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { PermissionGate } from "@/components/PermissionGate";
import { ApiPaths, api } from "@/lib/api";
import {
  ANNOTATION_COLORS,
  buildDraft,
  pointsToPath,
  toolToKind,
  type ToolKind,
} from "@/lib/annotation";
import { createAnnotationForEvidence } from "@/lib/annotationSync";
import { openCollabAnnotator } from "@/lib/evidenceView";
import { useSessionBus } from "@/lib/sessionBus";
import type {
  Annotation,
  AnnotationGeometry,
  EllipseGeometry,
  Evidence,
  FreehandGeometry,
  RectGeometry,
  SessionStatus,
} from "@/lib/types";

type Detail = Evidence & {
  download_url: string | null;
  annotations: Annotation[];
  /** Parent session status — `null` only for orphan captures. The
   *  modal switches to read-only (overlay toggle only, no editor
   *  tools, no "Annotate together") for any non-live status so a
   *  post-mortem review can't accidentally mutate the audit-chain
   *  artefact. "Live" = `open`/`recording`/`paused`. */
  session_status: SessionStatus | null;
};

const LIVE_SESSION_STATUSES: SessionStatus[] = ["open", "recording", "paused"];

function isSessionLive(s: SessionStatus | null | undefined): boolean {
  return s != null && LIVE_SESSION_STATUSES.includes(s);
}

type Props = { evidenceId: string; onClose: () => void };

export function EvidenceViewerModal({ evidenceId, onClose }: Props) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["evidence-detail", evidenceId],
    queryFn: async () => {
      const res = await api.get<Detail>(ApiPaths.evidence(evidenceId));
      return res.data;
    },
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("evidence.dialogLabel")}
      // `overflow-y-auto` + `items-start` lets the page scroll when the
      // modal is taller than the viewport (e.g. portrait phone snapshot
      // with editor toolbar + metadata panel). `min-h-dvh` keeps the
      // dim background filling the whole window even when content is
      // short.
      className="fixed inset-0 z-50 flex min-h-dvh items-start justify-center overflow-y-auto bg-black/80 p-4"
      onClick={onClose}
    >
      <div
        // Cap to viewport height so the close button + header stay
        // reachable without page scroll on tall captures; the inner
        // body handles its own overflow. dvh tracks the iOS Safari
        // dynamic viewport so the close button stays reachable when
        // the address bar collapses.
        className="relative my-auto flex max-h-[calc(100dvh-2rem)] w-full max-w-4xl flex-col rounded-lg border border-nb-border bg-nb-bg p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-3 flex shrink-0 items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold">
              {q.data ? `${q.data.kind} · ${humanSize(q.data.byte_size)}` : "Evidence"}
            </h2>
            {q.data && (
              <p className="mt-1 break-all text-xs text-nb-text-3">
                <span className="font-mono">{q.data.sha256.slice(0, 16)}…</span>
                <span className="ml-2">
                  {q.data.integrity_jsonb.size_verified ? (
                    <span className="text-cvp-ok">size-verified</span>
                  ) : null}
                  {q.data.integrity_jsonb.sha256_verified ? (
                    <span className="ml-2 text-cvp-ok">sha256-verified</span>
                  ) : (
                    <span className="ml-2 text-cvp-warn">hash pending</span>
                  )}
                </span>
              </p>
            )}
          </div>
          <button className="btn-ghost" onClick={onClose}>
            Close
          </button>
        </header>

        {/* Scrollable body — header stays pinned at the top, contents
            scroll inside the card so the close button is always
            reachable on tall captures. */}
        <div className="-mr-1 flex-1 overflow-y-auto pr-1">
          {q.isLoading && <p className="text-sm text-nb-text-2">Loading…</p>}
          {q.isError && (
            <p role="alert" className="text-sm text-cvp-danger">
              Could not load evidence.
            </p>
          )}
          {q.data && <Composite evidence={q.data} onClose={onClose} />}
        </div>
      </div>
    </div>
  );
}


function Composite({
  evidence,
  onClose,
}: {
  evidence: Detail;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  if (evidence.status !== "ready" || !evidence.download_url) {
    return (
      <div className="rounded-md border border-nb-border bg-nb-surface/40 p-6 text-center text-sm text-nb-text-2">
        This evidence is in <span className="text-cvp-warn">{evidence.status}</span>.
        The download URL is only available once /complete has verified the upload.
      </div>
    );
  }

  const isVideo = evidence.mime.startsWith("video/");
  if (isVideo) {
    // Recording playback. Annotated derivatives have annotations
    // burned into pixels, so no overlay here.
    return (
      <div className="space-y-3">
        <div className="relative w-full overflow-hidden rounded border border-nb-border bg-black">
          <video
            src={evidence.download_url}
            controls
            preload="metadata"
            className="block max-h-[70dvh] w-full bg-black"
          />
        </div>
        <Metadata evidence={evidence} />
      </div>
    );
  }

  // Documents (signed/source PDFs shared in chat, attachments) are NOT
  // images — render the PDF in the browser's native viewer with a
  // prominent download, never as an <img>.
  const isPdf = evidence.mime === "application/pdf";
  if (isPdf) {
    return (
      <div className="space-y-3">
        <div className="flex justify-end">
          <a
            href={evidence.download_url}
            target="_blank"
            rel="noreferrer"
            download
            className="btn-ghost text-sm"
            data-testid="evidence-download-pdf"
          >
            {t("evidence.downloadPdf")} ↓
          </a>
        </div>
        <iframe
          title="PDF"
          src={evidence.download_url}
          className="h-[70dvh] w-full rounded border border-nb-border bg-white"
        />
        <Metadata evidence={evidence} />
      </div>
    );
  }

  // Any other non-image binary (zip, doc, …) — can't preview inline.
  const isImage = evidence.mime.startsWith("image/");
  if (!isImage) {
    return (
      <div className="space-y-3">
        <div className="rounded-md border border-nb-border bg-nb-surface/40 p-8 text-center text-sm">
          <p className="text-nb-text-2">{t("evidence.noPreview")}</p>
          <a
            href={evidence.download_url}
            target="_blank"
            rel="noreferrer"
            download
            className="btn-primary mt-3 inline-block text-sm"
            data-testid="evidence-download-file"
          >
            {t("evidence.download")} ↓
          </a>
        </div>
        <Metadata evidence={evidence} />
      </div>
    );
  }

  return <StillEditor evidence={evidence} onClose={onClose} />;
}


type Tool = ToolKind;
type Draft =
  | { kind: "freehand"; points: [number, number][]; color: string }
  | { kind: "rect"; start: [number, number]; cur: [number, number]; color: string }
  | { kind: "ellipse"; start: [number, number]; cur: [number, number]; color: string };

const DRAFT_STROKE_WIDTH = 0.006;

function StillEditor({
  evidence,
  onClose,
}: {
  evidence: Detail;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const bus = useSessionBus((s) => s.bus);
  const [tool, setTool] = useState<Tool>("off");
  const [color, setColor] = useState<string>(ANNOTATION_COLORS[0]);
  // Default to OFF — the first thing the viewer should see is the raw
  // captured frame (the actual image bytes; the evidence). Bound
  // annotations are a vector OVERLAY, not part of the image, and the
  // default-on version made them look fused with the image and made it
  // unclear that they were a separate layer. The user can toggle them
  // on to inspect prior marks or to draw new ones on top.
  const [showAnnotations, setShowAnnotations] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const imgRef = useRef<HTMLImageElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  // Pixel-bounds of the image's drawn rect inside the wrap. The <img>
  // is `block w-full` so its rendered width = the wrap width, height
  // = wrap-width * (intrinsic_h / intrinsic_w). The SVG overlay sizes
  // to match so normalised [0,1] coords land on the actual image
  // pixels regardless of the modal's responsive sizing.
  const [imageBox, setImageBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null);

  useEffect(() => {
    const img = imgRef.current;
    const wrap = wrapRef.current;
    if (!img || !wrap) return;

    function update() {
      const i = imgRef.current;
      const w = wrapRef.current;
      if (!i || !w) return;
      if (!i.naturalWidth || !i.naturalHeight) return;
      const wRect = w.getBoundingClientRect();
      const iRect = i.getBoundingClientRect();
      setImageBox({
        left: iRect.left - wRect.left,
        top: iRect.top - wRect.top,
        width: iRect.width,
        height: iRect.height,
      });
    }

    update();
    const ro = new ResizeObserver(update);
    ro.observe(wrap);
    ro.observe(img);
    img.addEventListener("load", update);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      img.removeEventListener("load", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const armed = tool !== "off";

  const create = useMutation({
    mutationFn: async (draftIn: { kind: ReturnType<typeof toolToKind>; geometry: AnnotationGeometry; color: string }) => {
      const payload = buildDraft(draftIn.kind, draftIn.geometry, draftIn.color);
      const annotation = await createAnnotationForEvidence(
        evidence.session,
        evidence.id,
        {
          kind: payload.kind,
          geometry: payload.vector_jsonb,
          color: payload.color,
          stroke_width: payload.stroke_width,
        },
      );
      return annotation;
    },
    onSuccess: () => {
      // Refetch the evidence detail so the new annotation shows up in
      // the overlay's list of bound annotations.
      void qc.invalidateQueries({ queryKey: ["evidence-detail", evidence.id] });
      // And the list view, which already polls.
      void qc.invalidateQueries({ queryKey: ["session-evidence", evidence.session] });
    },
    onError: (e) => setErr((e as Error).message),
  });

  function pointFromEvent(e: React.PointerEvent): [number, number] | null {
    const target = e.currentTarget as SVGSVGElement;
    const rect = target.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    return [clamp01(x), clamp01(y)];
  }

  function onPointerDown(e: React.PointerEvent) {
    if (!armed) return;
    const p = pointFromEvent(e);
    if (!p) return;
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    if (tool === "pen") setDraft({ kind: "freehand", points: [p], color });
    else if (tool === "rect") setDraft({ kind: "rect", start: p, cur: p, color });
    else if (tool === "ellipse") setDraft({ kind: "ellipse", start: p, cur: p, color });
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!armed) return;
    const p = pointFromEvent(e);
    if (!p) return;
    setDraft((prev) => {
      if (!prev) return prev;
      if (prev.kind === "freehand") return { ...prev, points: [...prev.points, p] };
      return { ...prev, cur: p };
    });
  }

  function onPointerUp() {
    if (!armed || !draft) return;
    const finalised = draft;
    setDraft(null);
    if (finalised.kind === "freehand" && finalised.points.length < 2) return;
    if (
      (finalised.kind === "rect" || finalised.kind === "ellipse") &&
      pointsAlmostEqual(finalised.start, finalised.cur)
    ) {
      return;
    }
    const geometry = draftGeometry(finalised);
    create.mutate({ kind: toolToKind(tool), geometry, color: finalised.color });
  }

  // Reset draft if the tool changes mid-drag.
  useEffect(() => {
    setDraft(null);
  }, [tool]);

  const annotations = useMemo(() => evidence.annotations, [evidence.annotations]);
  const overlayStyle: React.CSSProperties = imageBox
    ? {
        position: "absolute",
        left: `${imageBox.left}px`,
        top: `${imageBox.top}px`,
        width: `${imageBox.width}px`,
        height: `${imageBox.height}px`,
      }
    : { position: "absolute", inset: 0 };

  // Closed (or otherwise non-live) sessions are read-only: the audit
  // chain is sealed, so the viewer surfaces ONLY the overlay toggle
  // and hides the drawing tools + "Annotate together" affordance.
  const live = isSessionLive(evidence.session_status);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        {live && (
          <EditorToolbar
            tool={tool}
            color={color}
            onTool={setTool}
            onColor={setColor}
            busy={create.isPending}
          />
        )}
        <label className="flex items-center gap-2 text-xs text-nb-text">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-600 bg-nb-surface text-nb-mint focus:ring-nb-mint"
            checked={showAnnotations}
            onChange={(e) => setShowAnnotations(e.target.checked)}
          />
          Overlay {annotations.length} annotation
          {annotations.length === 1 ? "" : "s"}
          <span className="text-nb-text-3">(vector — separate from image)</span>
        </label>
        {live && (
          <PermissionGate perm="session.annotation.create">
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => {
                // Flips both sides to the EvidenceAnnotator on this still.
                // The viewer modal closes underneath because the collab
                // annotator is full-screen — leaving the viewer mounted
                // would put two overlay layers on top of each other.
                openCollabAnnotator(bus, evidence.id);
                onClose();
              }}
              title={t("evidence.annotateTogetherTitle")}
              data-testid="viewer-annotate-together"
            >
              Annotate together
            </button>
          </PermissionGate>
        )}
        {!live && (
          <span className="ml-auto rounded bg-nb-surface/60 px-2 py-1 text-[11px] text-nb-text-2">
            Read-only · session {evidence.session_status ?? "closed"}
          </span>
        )}
      </div>

      <div
        ref={wrapRef}
        // Centred container so the image (which is constrained to fit
        // both the viewport height and the modal width below) sits in
        // the middle of the dark backdrop. The SVG annotation overlay
        // re-derives its rect from the rendered <img> via
        // getBoundingClientRect, so this layout works for landscape
        // and portrait captures alike.
        className="relative flex w-full items-center justify-center overflow-hidden rounded border border-nb-border bg-black"
      >
        <img
          ref={imgRef}
          src={evidence.download_url!}
          alt={`${evidence.kind} captured at ${evidence.captured_at}`}
          // `max-h-[70dvh]` matches the recording <video> cap so a
          // tall portrait phone snapshot doesn't blow out the modal
          // height. `object-contain` keeps the aspect ratio intact.
          className="block max-h-[70dvh] max-w-full object-contain"
        />
        {(showAnnotations || armed) && (
          <svg
            viewBox="0 0 1 1"
            preserveAspectRatio="none"
            style={overlayStyle}
            className={
              armed
                ? "pointer-events-auto z-10 cursor-crosshair touch-none"
                : "pointer-events-none z-10"
            }
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={() => setDraft(null)}
          >
            {showAnnotations &&
              annotations.map((a) => <BoundAnnotation key={a.id} a={a} />)}
            {draft && <DraftAnnotation draft={draft} />}
          </svg>
        )}
      </div>

      {err && (
        <p role="alert" className="text-xs text-cvp-danger">
          {err}
        </p>
      )}
      <Metadata evidence={evidence} />
    </div>
  );
}


function EditorToolbar({
  tool,
  color,
  onTool,
  onColor,
  busy,
}: {
  tool: Tool;
  color: string;
  onTool: (t: Tool) => void;
  onColor: (c: string) => void;
  busy: boolean;
}) {
  const TOOLS: { id: Tool; label: string; symbol: string }[] = [
    { id: "off", label: "Off (view only)", symbol: "↖" },
    { id: "pen", label: "Pen", symbol: "✏︎" },
    { id: "rect", label: "Rectangle", symbol: "▭" },
    { id: "ellipse", label: "Ellipse", symbol: "◯" },
  ];
  return (
    <div className="flex items-center gap-2 rounded-md border border-nb-border bg-nb-bg/80 p-1.5">
      <div className="flex gap-1" role="radiogroup" aria-label="Annotation tool">
        {TOOLS.map((t) => (
          <button
            key={t.id}
            role="radio"
            aria-checked={tool === t.id}
            aria-label={t.label}
            type="button"
            disabled={busy}
            onClick={() => onTool(t.id)}
            className={`h-8 w-8 rounded-md text-sm transition ${
              tool === t.id
                ? "bg-nb-mint text-black"
                : "text-nb-text hover:bg-nb-surface"
            } ${busy ? "cursor-not-allowed opacity-60" : ""}`}
          >
            {t.symbol}
          </button>
        ))}
      </div>
      <div className="flex gap-1" role="radiogroup" aria-label="Annotation color">
        {ANNOTATION_COLORS.map((c) => (
          <button
            key={c}
            role="radio"
            aria-checked={color === c}
            aria-label={`color ${c}`}
            type="button"
            disabled={busy}
            onClick={() => onColor(c)}
            style={{ backgroundColor: c }}
            className={`h-7 w-7 rounded-full border-2 transition ${
              color === c ? "border-white" : "border-transparent"
            } ${busy ? "cursor-not-allowed opacity-60" : ""}`}
          />
        ))}
      </div>
      {busy && <span className="text-xs text-nb-text-2">Saving…</span>}
    </div>
  );
}


function BoundAnnotation({ a }: { a: Annotation }) {
  const stroke = a.color;
  const sw = a.stroke_width;
  if (a.kind === "freehand") {
    const g = a.vector_jsonb as FreehandGeometry;
    return (
      <path
        d={pointsToPath(g.points)}
        fill="none"
        stroke={stroke}
        strokeWidth={sw}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    );
  }
  if (a.kind === "rect") {
    const g = a.vector_jsonb as RectGeometry;
    return (
      <rect
        x={Math.min(g.x, g.x + g.w)}
        y={Math.min(g.y, g.y + g.h)}
        width={Math.abs(g.w)}
        height={Math.abs(g.h)}
        fill="none"
        stroke={stroke}
        strokeWidth={sw}
      />
    );
  }
  const g = a.vector_jsonb as EllipseGeometry;
  return (
    <ellipse
      cx={g.cx}
      cy={g.cy}
      rx={g.rx}
      ry={g.ry}
      fill="none"
      stroke={stroke}
      strokeWidth={sw}
    />
  );
}


function DraftAnnotation({ draft }: { draft: Draft }) {
  if (draft.kind === "freehand") {
    return (
      <path
        d={pointsToPath(draft.points)}
        fill="none"
        stroke={draft.color}
        strokeWidth={DRAFT_STROKE_WIDTH}
        strokeLinejoin="round"
        strokeLinecap="round"
        opacity={0.9}
      />
    );
  }
  if (draft.kind === "rect") {
    const x = Math.min(draft.start[0], draft.cur[0]);
    const y = Math.min(draft.start[1], draft.cur[1]);
    const w = Math.abs(draft.cur[0] - draft.start[0]);
    const h = Math.abs(draft.cur[1] - draft.start[1]);
    return (
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        fill="none"
        stroke={draft.color}
        strokeWidth={DRAFT_STROKE_WIDTH}
        opacity={0.9}
      />
    );
  }
  const cx = (draft.start[0] + draft.cur[0]) / 2;
  const cy = (draft.start[1] + draft.cur[1]) / 2;
  const rx = Math.abs(draft.cur[0] - draft.start[0]) / 2;
  const ry = Math.abs(draft.cur[1] - draft.start[1]) / 2;
  return (
    <ellipse
      cx={cx}
      cy={cy}
      rx={rx}
      ry={ry}
      fill="none"
      stroke={draft.color}
      strokeWidth={DRAFT_STROKE_WIDTH}
      opacity={0.9}
    />
  );
}


function draftGeometry(d: Draft): AnnotationGeometry {
  if (d.kind === "freehand") return { points: d.points } satisfies FreehandGeometry;
  if (d.kind === "rect") {
    return {
      x: Math.min(d.start[0], d.cur[0]),
      y: Math.min(d.start[1], d.cur[1]),
      w: Math.abs(d.cur[0] - d.start[0]),
      h: Math.abs(d.cur[1] - d.start[1]),
    } satisfies RectGeometry;
  }
  return {
    cx: (d.start[0] + d.cur[0]) / 2,
    cy: (d.start[1] + d.cur[1]) / 2,
    rx: Math.abs(d.cur[0] - d.start[0]) / 2,
    ry: Math.abs(d.cur[1] - d.start[1]) / 2,
  } satisfies EllipseGeometry;
}


function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}


function pointsAlmostEqual(a: [number, number], b: [number, number]): boolean {
  return Math.abs(a[0] - b[0]) < 0.002 && Math.abs(a[1] - b[1]) < 0.002;
}


function Metadata({ evidence }: { evidence: Detail }) {
  const { t } = useTranslation();
  const geo = evidence.geo_jsonb as Record<string, unknown>;
  const hasGeo = geo && typeof geo.lat === "number";
  return (
    <dl className="grid grid-cols-2 gap-2 rounded border border-nb-border bg-nb-surface/30 p-3 text-xs">
      <Row label="Kind" value={evidence.kind} />
      <Row label="MIME" value={evidence.mime} />
      <Row label="Captured" value={evidence.captured_at} />
      <Row label="Status" value={evidence.status} />
      <Row label="Annotations" value={String(evidence.annotations.length)} />
      <Row
        label="Author"
        value={
          evidence.uploaded_by !== null
            ? `user-${evidence.uploaded_by}`
            : evidence.uploaded_by_participant
              ? `participant-${(evidence.uploaded_by_participant as string).slice(0, 8)}…`
              : "—"
        }
      />
      <Row
        label={t("evidence.geoLabel")}
        value={
          hasGeo
            ? `${(geo.lat as number).toFixed(5)}, ${(geo.lng as number).toFixed(5)} (±${geo.accuracy_m ?? "?"} m, ${geo.source ?? "unknown"})`
            : t("evidence.geoUnavailable")
        }
        muted={!hasGeo}
      />
      <Row
        label="Size"
        value={`${humanSize(evidence.byte_size)} · ${evidence.byte_size.toLocaleString()} bytes`}
      />
    </dl>
  );
}

function Row({
  label,
  value,
  muted = false,
}: {
  label: string;
  value: string;
  muted?: boolean;
}) {
  return (
    <>
      <dt className="text-nb-text-3">{label}</dt>
      <dd
        className={`break-words ${muted ? "italic text-nb-text-3" : "text-nb-text"}`}
      >
        {value}
      </dd>
    </>
  );
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}